/** Member-level status. NOTE: VAIR Pro / vairified are per-sport, not here. */
export interface VairUserStatus {
  isWheelchair: boolean;
  isAmbassador: boolean;
  /** Player has an active OAuth connection to us. */
  isConnected: boolean;
}

export interface VairSportRatingSplit {
  rating: number;
  abbr: string;
}

/** VAIR Pro lifecycle: ACTIVE = approved & can rate, PENDING = paid, awaiting approval. */
export type VairProStatus = 'PENDING' | 'ACTIVE';

/**
 * Per-sport rating data. VAIR Pro status is scoped to a sport — a Pro in
 * pickleball is not automatically a Pro in padel/table tennis (each sport needs
 * its own coach certificate + rater training).
 *
 * VAIR also returns a legacy `isRater` alias of `isVairPro`, which they intend
 * to delete — deliberately not modelled here so its removal can't break us.
 */
export interface VairSportRating {
  rating: number;
  abbr: string;
  ratingSplits: Record<string, VairSportRatingSplit>;
  /** Verified (non-recreational) rating in THIS sport. */
  isVairified: boolean;
  /** Active VAIR Pro (can rate) in THIS sport. False while PENDING. */
  isVairPro: boolean;
  /** `null` when the player is not a VAIR Pro in this sport. */
  isVairProStatus: VairProStatus | null;
}

export interface VairUserSports {
  pickleball?: VairSportRating;
  [sportCode: string]: VairSportRating | undefined;
}

export type VairGender = 'MALE' | 'FEMALE' | 'OTHER' | 'UNKNOWN';

/**
 * Single player record from the VAIR partner members API. Only
 * memberId/names/status/sport are guaranteed — the rest are scope-dependent or
 * simply omitted from the response, so they must stay optional.
 */
export interface VairUser {
  memberId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  displayName: string;
  status: VairUserStatus;
  sport: VairUserSports;
  age?: number;
  city?: string;
  state?: string;
  zip?: string;
  /** ISO-3166 alpha-2 */
  country?: string;
  gender?: VairGender;
  activeLeagues?: string[];
  /** Only present when the `profile:email` scope is granted. */
  email?: string;
  grantedScopes?: string[];
}

export interface VairPlayersPayload {
  userIds: string[];
  sport?: string;
}

/** Response from the players API: bounceUserId → VairUser */
export type VairPlayerSearchResponse = Record<string, VairUser>;

export const VAIR_PLAYER_SEARCH_API = 'v1/vair/players';
